import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  BucketIcon,
  File01Icon,
  UserAdd01Icon,
  UserRemove02Icon,
  UserStar02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { NonCustomerAccountTypes } from 'constants/account-types';
import { AdminPermissions } from 'constants/admin-permissions';
import { countries } from 'constants/countries';
import { CC_STATUS_DISPLAY } from 'constants/statuses';
import { CC_STATUS_ICON_DISPLAY } from 'constants/statuses';
import {
  VIEW_FILTER_OPTIONS,
  WORKING_QUEUE_ENFORCED,
  buildAdminChecksDefaultQuery,
  isSubcheckView,
  resolveAdminChecksView,
  submittedWithin90DaysFilter,
} from 'helpers/admin-candidate-checks';
import {
  readAdminChecksSession,
  writeAdminChecksSession,
} from 'helpers/admin-candidate-checks-session';
import {
  ADMIN_CHECK_TYPE_MULTI_SELECT_NORMALIZE,
  buildCheckTypeFilterOptions,
} from 'helpers/check-type-filter-options';
import { isDateDue } from 'helpers/date';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import { useSupportUserNames } from 'hooks/use-support-user-names';
import AdminPage from 'layouts/AdminPage/AdminPage';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ActivityCountIcons } from 'views/AdminApp/AdminCandidate/components/CandidateCheckDetails/CandidateCheckDetails';

import { BulkAssignSupportUsersModal } from './components/BulkAssignSupportUsersModal/BulkAssignSupportUsersModal';
import { ChecksSidebarWrapper } from './components/ChecksSidebarWrapper/ChecksSidebarWrapper';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { BulkActionsBar } from 'components/BulkActionsBar/BulkActionsBar';
import { Button, FilePicker } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import StatusPill from 'components/Pills/StatusPills';
import { Table, useTableContext } from 'components/Table/Table';
import { CustomToast } from 'components/Toast/CustomToast';

import { selectAuthentication } from 'api/authentication-slice';
import {
  candidateChecksApi,
  useBulkAssignmentSummaryMutation,
  useBulkUpdateSupportUsersMutation,
  useGetAdminKeyDetailsQuery,
  useGetAdminSidebarCountsQuery,
  useLazyDownloadAdminCandidateChecksReportQuery,
  useLazyGetAllCandidateChecksQuery,
} from 'api/candidate-checks-api';
import { useGetCheckTypesQuery } from 'api/check-types-api';
import { useGetAllCompaniesListQuery } from 'api/company-api';
import { useLazyGetACICExportFileQuery, useLazySignACICResultUploadQuery } from 'api/services-api';
import {
  useBulkSubCheckAssignmentSummaryMutation,
  useBulkUpdateSubCheckSupportUsersMutation,
} from 'api/sub-checks-api';
import { getUser, selectUser } from 'api/user-slice';

import { ActiveViewBanner } from '../../Reporting/components/SavedViews/FiltersChangedBanner';
import { ReportingSavedViewsTableSync } from '../../Reporting/components/SavedViews/ReportingSavedViewsIntegrations';
import { SavedViewsDropdown } from '../../Reporting/components/SavedViews/SavedViewsDropdown';
import { useSavedViews } from '../../Reporting/components/SavedViews/useSavedViews';
import { ActionRequiredIcons } from './ActionRequired';
import { CandidateCheckNotesModal } from './AdminCandidateCheckNotesModal';
import styles from './AdminCandidateChecks.module.scss';
import {
  AdminChecksAuditFilter,
  AdminChecksDateFilter,
  AdminChecksHelpFilter,
  AdminChecksIssuesFilter,
  AdminChecksStatusFilter,
  AdminChecksWorkFilter,
} from './AdminChecksFilters';
import { AdminChecksViewSync } from './AdminChecksViewSync';

const ApplyFiltersButton = () => {
  const { manuallyApplyFilters } = useTableContext();

  return (
    <Button icon="filter" onClick={manuallyApplyFilters}>
      Apply Filters
    </Button>
  );
};

const ChecksBulkActionsBarBridge = ({
  pagination,
  showSubchecks,
  supportUsers,
  bulkAssignmentSummary,
  bulkSubCheckAssignmentSummary,
  tableQuery,
  candidateChecks,
  onBulkAssign,
  onBulkUnassign,
}) => {
  const { selectedIds, bulkUseAll, setSelectedIds, setSelectedAll, setBulkUseAll } =
    useTableContext();

  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [unassignModalVisible, setUnassignModalVisible] = useState(false);

  const handleClose = () => {
    setSelectedIds([]);
    setSelectedAll(false);
    setBulkUseAll(false);
  };

  const targetDescriptor = { ids: selectedIds, useAll: bulkUseAll, filters: tableQuery };
  const targetLabel = showSubchecks ? 'sub-checks' : 'checks';
  const fetchSummary = showSubchecks ? bulkSubCheckAssignmentSummary : bulkAssignmentSummary;

  const rowId = row => (showSubchecks ? row.sub_check_id : row.candidate_check_id);
  const selectedRows = candidateChecks.filter(row => selectedIds.includes(rowId(row)));
  const canUnassign =
    tableQuery?.support_user !== 'unassigned' &&
    (bulkUseAll ||
      selectedRows.length < selectedIds.length ||
      selectedRows.some(row => row.support_user && row.support_user !== 'Unassigned'));

  return (
    <>
      <BulkAssignSupportUsersModal
        visible={assignModalVisible}
        mode="assign"
        supportUsers={supportUsers}
        targetDescriptor={targetDescriptor}
        targetLabel={targetLabel}
        allowIncludeSubChecks={!showSubchecks}
        fetchSummary={fetchSummary}
        onClose={() => setAssignModalVisible(false)}
        onSubmit={async (userIds, { replace, includeSubChecks }) => {
          setAssignModalVisible(false);
          const success = await onBulkAssign(userIds, selectedIds, bulkUseAll, {
            replace,
            includeSubChecks,
          });
          if (success) {
            handleClose();
          }
        }}
      />
      <BulkAssignSupportUsersModal
        visible={unassignModalVisible}
        mode="unassign"
        supportUsers={supportUsers}
        targetDescriptor={targetDescriptor}
        targetLabel={targetLabel}
        allowIncludeSubChecks={!showSubchecks}
        fetchSummary={fetchSummary}
        onClose={() => setUnassignModalVisible(false)}
        onSubmit={async (userIds, { includeSubChecks }) => {
          setUnassignModalVisible(false);
          const success = await onBulkUnassign(userIds, selectedIds, bulkUseAll, {
            includeSubChecks,
          });
          if (success) {
            handleClose();
          }
        }}
      />
      <BulkActionsBar
        active={selectedIds.length > 0}
        selectedIds={selectedIds}
        totalMatchingCount={pagination.total_records}
        onUseAllChange={setBulkUseAll}
        handleClose={handleClose}>
        <OverlayTrigger placement="top" overlay={<Tooltip>Assign to...</Tooltip>}>
          <div>
            <IconComponent
              icon={UserAdd01Icon}
              type="neutral"
              noBackground
              onClick={() => setAssignModalVisible(true)}
            />
          </div>
        </OverlayTrigger>
        {canUnassign && (
          <OverlayTrigger placement="top" overlay={<Tooltip>Unassign</Tooltip>}>
            <div>
              <IconComponent
                icon={UserRemove02Icon}
                type="neutral"
                noBackground
                onClick={() => setUnassignModalVisible(true)}
              />
            </div>
          </OverlayTrigger>
        )}
      </BulkActionsBar>
    </>
  );
};

const AdminCandidateChecks = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  const authentication = useSelector(selectAuthentication);
  const hasPermission = useAccessControl(authentication.admin_permissions);
  const user = useSelector(selectUser);

  const [candidateChecks, setCandidateChecks] = useState([]);
  const hasLoadedChecksRef = useRef(false);
  const [companiesOptions, setCompaniesOptions] = useState([]);

  const restoredSessionRef = useRef(undefined);
  if (restoredSessionRef.current === undefined) {
    restoredSessionRef.current = readAdminChecksSession(authentication.user_id || user?.id) || null;
  }
  const restoredSession = restoredSessionRef.current;

  const [selectedView, setSelectedView] = useState(() =>
    resolveAdminChecksView(restoredSession?.queryParams?.view?.value),
  );

  const [activeSmartFilterId, setActiveSmartFilterId] = useState(
    () => restoredSession?.activeSmartFilterId ?? null,
  );

  const [showNotes, setShowNotes] = useState(false);
  const [selectedCheckId, setSelectedCheckId] = useState(null);
  const [showUploadACICResultModal, setShowUploadACICResultModal] = useState(false);
  const [acicSkippedCount, setAcicSkippedCount] = useState(null);

  const [getAllCandidateChecks] = useLazyGetAllCandidateChecksQuery();
  const [downloadAdminCandidateChecksReport] = useLazyDownloadAdminCandidateChecksReportQuery();
  const [getACICExportFile] = useLazyGetACICExportFileQuery();
  const [signACICResultUpload] = useLazySignACICResultUploadQuery();
  const { data: companiesData } = useGetAllCompaniesListQuery();
  const { data: checkTypes } = useGetCheckTypesQuery();
  const { data: keyDetailOptions = [] } = useGetAdminKeyDetailsQuery();

  const [checkType, setCheckType] = useState();

  useDocumentTitle('Candidate Checks');

  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  const OVERDUE_SUPPORT_HOURS = 15;
  const showSubchecks = isSubcheckView(selectedView);
  const { data: sidebarCounts } = useGetAdminSidebarCountsQuery({
    overdue_support_hours: OVERDUE_SUPPORT_HOURS,
  });
  const prioritizeAssignedToMe = (sidebarCounts?.my_checks ?? 0) > 0;
  const currentSupportUserId = authentication.user_id || user?.id;
  const currentSupportUserName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');

  const hardcodedDefaultQuery = buildAdminChecksDefaultQuery({
    statusNames: [t('common.inProgress'), t('common.submitted'), t('common.deferred')],
  });

  const defaultQuery = restoredSession?.queryParams || hardcodedDefaultQuery;

  const [tableQuery, setTableQuery] = useState(() => {
    if (!restoredSession?.queryParams) return hardcodedDefaultQuery;
    const flat = {};
    Object.keys(restoredSession.queryParams).forEach(key => {
      const stored = restoredSession.queryParams[key];
      if (stored && typeof stored === 'object' && 'value' in stored) {
        flat[key] = stored.value;
      } else {
        flat[key] = stored;
      }
    });
    return flat;
  });

  // Sidebar views apply defining filters once via pendingApply. Keep the tab highlighted
  // while the query still matches those params; if the admin changes them, clear the tab
  // instead of re-forcing (so filters stay visible and editable).
  const smartFilterEnforcedParams = {
    all_checks: {
      view: 'checks',
      ...WORKING_QUEUE_ENFORCED,
    },
    all_sub_checks: {
      view: 'subchecks',
      ...WORKING_QUEUE_ENFORCED,
    },
    my_checks: {
      view: 'checks',
      my_checks: 1,
      ...WORKING_QUEUE_ENFORCED,
    },
    my_sub_checks: {
      view: 'subchecks',
      my_checks: 1,
      ...WORKING_QUEUE_ENFORCED,
    },
    intercom: {
      view: 'checks',
      work: 'intercom',
      ...(prioritizeAssignedToMe && currentSupportUserId
        ? { support_user: String(currentSupportUserId) }
        : {}),
      ...WORKING_QUEUE_ENFORCED,
    },
    next_action_due: {
      view: 'checks',
      work: 'due',
      ...(prioritizeAssignedToMe && currentSupportUserId
        ? { support_user: String(currentSupportUserId) }
        : {}),
      ...WORKING_QUEUE_ENFORCED,
    },
    overdue_provider: {
      view: 'checks',
      work: 'overdue_provider',
      ...(prioritizeAssignedToMe && currentSupportUserId
        ? { support_user: String(currentSupportUserId) }
        : {}),
      ...WORKING_QUEUE_ENFORCED,
    },
    overdue_support: {
      view: 'checks',
      work: 'overdue_support',
      ...(prioritizeAssignedToMe && currentSupportUserId
        ? { support_user: String(currentSupportUserId) }
        : {}),
      ...WORKING_QUEUE_ENFORCED,
    },
    id_requirements: {
      view: 'checks',
      issues: 'id_not_met',
      submitted_at: submittedWithin90DaysFilter().value,
      ...WORKING_QUEUE_ENFORCED,
    },
    bot_failed: {
      view: 'checks',
      issues: 'failed',
      submitted_at: submittedWithin90DaysFilter().value,
      ...WORKING_QUEUE_ENFORCED,
    },
    email_failed: {
      view: 'checks',
      issues: 'candidate_email_failed,provider_email_failed',
      submitted_at: submittedWithin90DaysFilter().value,
      ...WORKING_QUEUE_ENFORCED,
    },
    missing_result: {
      view: 'checks',
      check_type: WORKING_QUEUE_ENFORCED.check_type,
      status: 'completed',
      audit: 'no_upload',
      submitted_at: submittedWithin90DaysFilter().value,
    },
    unassigned: {
      view: 'checks',
      audit: 'unassigned',
      submitted_at: submittedWithin90DaysFilter().value,
    },
  };

  const normalizeFilterValue = value => {
    if (value == null || value === '') return '';
    const parts = Array.isArray(value) ? value : String(value).split(',');
    return parts
      .map(String)
      .map(part => part.trim())
      .filter(Boolean)
      .sort()
      .join(',');
  };

  const queryMatchesSmartFilter = (query, enforcedParams) =>
    Object.entries(enforcedParams).every(
      ([key, expected]) => normalizeFilterValue(query?.[key]) === normalizeFilterValue(expected),
    );

  const savedViewsState = useSavedViews('admin_candidate_checks');
  const sessionRestoreAppliedRef = useRef(false);
  const skippedDefaultForRestoreRef = useRef(false);

  if (restoredSession && !skippedDefaultForRestoreRef.current) {
    skippedDefaultForRestoreRef.current = true;
    savedViewsState.defaultViewApplyOnceRef.current = true;
  }

  useEffect(() => {
    if (sessionRestoreAppliedRef.current || !restoredSession) return;
    sessionRestoreAppliedRef.current = true;
    savedViewsState.defaultViewApplyOnceRef.current = true;
    if (restoredSession.activeViewId != null) {
      savedViewsState.setActiveViewId(restoredSession.activeViewId);
    }
    savedViewsState.setPendingApply({
      params: restoredSession.queryParams,
      key: Date.now(),
    });
    if (restoredSession.queryParams?.view?.value) {
      setSelectedView(resolveAdminChecksView(restoredSession.queryParams.view.value));
    }
  }, [
    restoredSession,
    savedViewsState.defaultViewApplyOnceRef,
    savedViewsState.setActiveViewId,
    savedViewsState.setPendingApply,
  ]);

  const persistSession = useCallback(
    (fullQueryParams, smartFilterId, activeViewId) => {
      const userId = authentication.user_id || user?.id;
      if (!userId || !fullQueryParams) return;
      writeAdminChecksSession(userId, {
        queryParams: fullQueryParams,
        activeSmartFilterId: smartFilterId ?? null,
        activeViewId: activeViewId ?? null,
      });
    },
    [authentication.user_id, user?.id],
  );

  const handleSavedViewSelect = (id, params) => {
    setActiveSmartFilterId(null);
    savedViewsState.setActiveViewId(id);
    savedViewsState.setPendingApply({ params, key: Date.now() });
  };

  const activeSavedView = savedViewsState.savedViews.find(
    v => Number(v.id) === Number(savedViewsState.activeViewId),
  );
  const savedViewBanner = activeSavedView ? (
    <ActiveViewBanner
      viewName={activeSavedView.name}
      activeViewData={activeSavedView}
      bannerViewQuerySynced={savedViewsState.bannerViewQuerySynced}
      onClearView={() => {
        savedViewsState.setActiveViewId(null);
        savedViewsState.setPendingApply(null);
      }}
      onRestore={() => {}}
      onUpdate={savedViewsState.handleUpdate}
      onSaveNew={() => savedViewsState.setSaveViewModal(true)}
      onSetDefault={savedViewsState.handleSetDefault}
    />
  ) : null;

  const { supportUsers } = useSupportUserNames();

  const checkTypeFilterOptions = useMemo(
    () =>
      buildCheckTypeFilterOptions(checkTypes, {
        includeSupportChecks: true,
        excludeDeprecated: true,
      }),
    [checkTypes],
  );

  const dispatch = useDispatch();
  const [bulkUpdateSupportUsers] = useBulkUpdateSupportUsersMutation();
  const [bulkUpdateSubCheckSupportUsers] = useBulkUpdateSubCheckSupportUsersMutation();
  const [bulkAssignmentSummary] = useBulkAssignmentSummaryMutation();
  const [bulkSubCheckAssignmentSummary] = useBulkSubCheckAssignmentSummaryMutation();

  useEffect(() => {
    if (!user?.id && authentication.user_id) {
      dispatch(getUser(authentication.user_id));
    }
  }, [authentication.user_id, user, dispatch]);

  const refetchSidebarCounts = () => {
    dispatch(
      candidateChecksApi.endpoints.getAdminSidebarCounts.initiate(
        { overdue_support_hours: OVERDUE_SUPPORT_HOURS },
        { forceRefetch: true },
      ),
    );
  };

  const showBulkResultToast = result => {
    const updatedCount = result?.updated_count ?? 0;
    const skippedCount = result?.skipped_count ?? 0;
    toast.custom(
      t => (
        <CustomToast
          t={t}
          title="Support users updated"
          message={
            skippedCount > 0
              ? `${updatedCount} of ${updatedCount + skippedCount} updated successfully. ${skippedCount} skipped because they’re not eligible for an update.`
              : `${updatedCount} check${updatedCount === 1 ? '' : 's'} updated successfully.`
          }
          type="success"
        />
      ),
      { duration: 6000 },
    );
  };

  const showBulkErrorToast = (title, error) => {
    toast.custom(
      t => (
        <CustomToast
          t={t}
          title={title}
          message={String(
            error?.data?.message || error?.data?.error || error?.message || 'Unknown error',
          )}
          type="error"
        />
      ),
      { duration: 5000 },
    );
  };

  const handleBulkAssignSubmit = async (
    userIds,
    ids,
    useAll,
    { replace = false, includeSubChecks = false } = {},
  ) => {
    try {
      const bulkMutation = showSubchecks ? bulkUpdateSubCheckSupportUsers : bulkUpdateSupportUsers;
      const result = await bulkMutation({
        ids,
        use_all: useAll,
        filters: tableQuery,
        add_user_ids: userIds,
        replace,
        ...(!showSubchecks ? { include_sub_checks: includeSubChecks } : {}),
      }).unwrap();
      showBulkResultToast(result);
      refetchSidebarCounts();
      fetchCandidateChecks(tableQuery);
      return true;
    } catch (error) {
      showBulkErrorToast('Error assigning support users', error);
      return false;
    }
  };

  const handleBulkUnassignSubmit = async (
    userIds,
    ids,
    useAll,
    { includeSubChecks = false } = {},
  ) => {
    try {
      const bulkMutation = showSubchecks ? bulkUpdateSubCheckSupportUsers : bulkUpdateSupportUsers;
      const result = await bulkMutation({
        ids,
        use_all: useAll,
        filters: tableQuery,
        remove_user_ids: userIds,
        ...(!showSubchecks ? { include_sub_checks: includeSubChecks } : {}),
      }).unwrap();
      showBulkResultToast(result);
      refetchSidebarCounts();
      fetchCandidateChecks(tableQuery);
      return true;
    } catch (error) {
      showBulkErrorToast('Error unassigning support users', error);
      return false;
    }
  };

  useEffect(() => {
    if (!companiesData) return;
    const list = Array.isArray(companiesData.result) ? companiesData.result : [];
    setCompaniesOptions([
      { name: t('common.all', { element: 'companies' }), value: 'all' },
      ...list.map(c => ({ name: c?.name ?? '', value: c?.id ?? '' })),
    ]);
  }, [companiesData]);

  const fetchCandidateChecks = async params => {
    // Only block the page on the first load — search/filter refetches update the table in place.
    if (!hasLoadedChecksRef.current) setLoading(true);
    try {
      const view = resolveAdminChecksView(params?.view);
      const { result } = await getAllCandidateChecks({
        ...params,
        view,
        overdue_support_hours: OVERDUE_SUPPORT_HOURS,
      }).unwrap();
      setPagination(result?.pagination);
      setCandidateChecks(Array.isArray(result?.candidate_checks) ? result.candidate_checks : []);
      hasLoadedChecksRef.current = true;
      if (params?.check_type && params?.check_type !== 'all')
        setCheckType((checkTypes || [])?.find(checkType => checkType.slug === params?.check_type));
      setLoading(false);
    } catch (error) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Error fetching checks"
            message={String(
              error?.data?.message || error?.data?.error || error?.message || 'Unknown error',
            )}
            type="error"
          />
        ),
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const formatMySqlUtcToLocalDate = ts => {
    if (!ts) return null;
    return new Date(ts.replace(' ', 'T') + 'Z');
  };

  const mapCCStatus = useCallback(status => {
    return CC_STATUS_DISPLAY[status]?.color || 'neutral';
  }, []);

  const download = async () => {
    setLoading(true);
    await downloadAdminCandidateChecksReport({
      queryParams: tableQuery,
      overdue_support_hours: OVERDUE_SUPPORT_HOURS,
    }).unwrap();
    setLoading(false);
  };

  const handleDownloadACICExportFile = async () => {
    setLoading(true);
    setAcicSkippedCount(null);
    try {
      const result = await getACICExportFile({}).unwrap();
      const skippedCount = result?.skippedCount ?? 0;
      if (skippedCount > 0) {
        setAcicSkippedCount(skippedCount);
      }
    } catch (error) {
      toast.custom(t => <CustomToast t={t} title="Error downloading ACIC export file" />);
    }
    setLoading(false);
  };

  return (
    <AdminPage
      loading={loading}
      title="Checks"
      allowedRole={AdminPermissions.ListCandidateChecks}
      sidebar={
        <ChecksSidebarWrapper
          savedViewsState={savedViewsState}
          activeSmartFilterId={activeSmartFilterId}
          setActiveSmartFilterId={setActiveSmartFilterId}
          overdueSupportHours={OVERDUE_SUPPORT_HOURS}
          skipInitialDefault={Boolean(restoredSession)}
          currentSupportUser={
            currentSupportUserId
              ? { id: currentSupportUserId, name: currentSupportUserName || 'Me' }
              : null
          }
        />
      }>
      <div className={styles.main}>
        {checkTypes?.length > 0 && (
          <Table.Context
            defaultQuery={defaultQuery}
            manualQueryUpdate={true}
            suppressQueryParamEffect={!savedViewsState.savedViewsReady}
            multiSelectNormalize={{
              check_type: ADMIN_CHECK_TYPE_MULTI_SELECT_NORMALIZE,
            }}
            onQueryUpdate={(query, fullQueryParams) => {
              const enforcedParams = smartFilterEnforcedParams[activeSmartFilterId];
              let smartFilterId = activeSmartFilterId;
              let nextQuery = query;
              let nextFullQueryParams = fullQueryParams;

              if (nextQuery?.support_user && nextQuery?.my_checks) {
                const { my_checks: _ignoredQuery, ...restQuery } = nextQuery;
                nextQuery = restQuery;
                if (nextFullQueryParams?.my_checks != null) {
                  const { my_checks: _ignoredFull, ...restFull } = nextFullQueryParams;
                  nextFullQueryParams = restFull;
                }
                if (enforcedParams?.my_checks != null) {
                  smartFilterId = null;
                  setActiveSmartFilterId(null);
                }
              }

              if (enforcedParams && !queryMatchesSmartFilter(nextQuery, enforcedParams)) {
                smartFilterId = null;
                setActiveSmartFilterId(null);

                // Only strip when leaving a My Work tab — my_checks is hidden and
                // otherwise survives. Keep it for saved views / session restore.
                if (enforcedParams.my_checks != null) {
                  if (nextQuery?.my_checks != null) {
                    const { my_checks: _ignoredQuery, ...restQuery } = nextQuery;
                    nextQuery = restQuery;
                  }
                  if (nextFullQueryParams?.my_checks != null) {
                    const { my_checks: _ignoredFull, ...restFull } = nextFullQueryParams;
                    nextFullQueryParams = restFull;
                  }
                  setTableQuery(nextQuery);
                  persistSession(
                    nextFullQueryParams || nextQuery,
                    smartFilterId,
                    savedViewsState.activeViewId,
                  );
                  savedViewsState.setPendingApply({
                    params: nextFullQueryParams || nextQuery,
                    key: Date.now(),
                  });
                  return;
                }
              }

              setTableQuery(nextQuery);
              fetchCandidateChecks(nextQuery);
              persistSession(
                nextFullQueryParams || nextQuery,
                smartFilterId,
                savedViewsState.activeViewId,
              );
            }}>
            <ReportingSavedViewsTableSync
              tab="admin_candidate_checks"
              savedViewsState={savedViewsState}
            />
            <AdminChecksViewSync onViewChange={setSelectedView} />
            <ChecksBulkActionsBarBridge
              pagination={pagination}
              showSubchecks={showSubchecks}
              supportUsers={supportUsers}
              bulkAssignmentSummary={bulkAssignmentSummary}
              bulkSubCheckAssignmentSummary={bulkSubCheckAssignmentSummary}
              tableQuery={tableQuery}
              candidateChecks={candidateChecks}
              onBulkAssign={handleBulkAssignSubmit}
              onBulkUnassign={handleBulkUnassignSubmit}
            />
            <div className="flex justify-between items-center m-12 bg-neutral-1 rounded-3xl border border-neutral-4 p-8">
              <div className="flex items-center gap-4">
                <IconComponent size="medium" icon={UserStar02Icon} />
                <div className="flex flex-col">
                  <span className="text-lg text-text-tertiary">Welcome,</span>
                  <span className="text-2xl font-medium text-neutral-7">
                    {user?.first_name} {user?.last_name}
                  </span>
                </div>
              </div>
              {savedViewsState.savedViews.length > 0 && (
                <SavedViewsDropdown
                  savedViews={savedViewsState.savedViews}
                  activeViewId={savedViewsState.activeViewId}
                  onSelectView={handleSavedViewSelect}
                  onSetDefault={savedViewsState.handleSetDefault}
                  onManage={() => savedViewsState.setManageViewsOpen(true)}
                  tab="admin_candidate_checks"
                />
              )}
            </div>
            <Table.FloatingFilters
              className="u-margin--large surface-white"
              banner={savedViewBanner}
              actions={
                <Button
                  type="secondary"
                  icon="bookmark"
                  onClick={() => savedViewsState.setSaveViewModal(true)}>
                  Save View
                </Button>
              }>
              <Table.FloatingFiltersLeft className="u-gap-medium align-items-end">
                <Table.Filter
                  title="View"
                  filterName="view"
                  options={VIEW_FILTER_OPTIONS}
                  useDefault={false}
                />
                <Table.MultiSelectFilter
                  title="Type"
                  filterName="check_type"
                  type="status"
                  options={checkTypeFilterOptions}
                  useSearchInMultiSelect={true}
                />
                {!showSubchecks && keyDetailOptions.length > 0 && (
                  <Table.MultiSelectFilter
                    title="Key Info"
                    filterName="key_detail"
                    type="status"
                    useSearchInMultiSelect={true}
                    options={keyDetailOptions}
                  />
                )}
                {showSubchecks && (
                  <Table.MultiSelectFilter
                    title="Country"
                    filterName="sub_check_title"
                    type="status"
                    useSearchInMultiSelect={true}
                    options={countries.map(country => ({
                      name: country.name,
                      value: `${country.alpha2}|${country.name}`,
                    }))}
                  />
                )}
                <Table.Filter
                  title="Company"
                  filterName="company"
                  options={companiesOptions}
                  useDefault={false}
                  useSearch={true}
                />
                <Table.Filter
                  title="Support User"
                  filterName="support_user"
                  type="status"
                  options={[
                    { name: t('common.all', { element: 'support users' }), value: 'all' },
                    { name: 'Unassigned', value: 'unassigned' },
                    ...(supportUsers || []).map(supportUser => ({
                      name: `${supportUser.first_name} ${supportUser.last_name}`,
                      value: supportUser.id,
                    })),
                  ]}
                  useDefault={false}
                  useSearch={true}
                />
                <AdminChecksStatusFilter />
                <AdminChecksDateFilter />
                <AdminChecksWorkFilter
                  overdueSupportHours={OVERDUE_SUPPORT_HOURS}
                  showSubchecks={showSubchecks}
                />
                <AdminChecksIssuesFilter showSubchecks={showSubchecks} />
                <AdminChecksHelpFilter />
                <AdminChecksAuditFilter />
              </Table.FloatingFiltersLeft>
              <Table.FloatingFiltersRight>
                <ApplyFiltersButton onApply={fetchCandidateChecks} />
              </Table.FloatingFiltersRight>
            </Table.FloatingFilters>
            <Table.TableContent className="u-margin--large">
              <Table.Filters>
                <Table.LeftFilters>
                  <Table.Search placeholder="Search name, email, or Intercom id" />
                </Table.LeftFilters>
                <Table.RightFilters>
                  <div className="flex items-center gap-2">
                    {hasPermission(AdminPermissions.ProcessACIC) && (
                      <>
                        <Button
                          icon="download"
                          onClick={handleDownloadACICExportFile}
                          small
                          type="secondary-reverse">
                          Download ACIC XML
                        </Button>
                        <Button
                          icon="upload"
                          onClick={() => setShowUploadACICResultModal(true)}
                          small
                          type="secondary-reverse">
                          Upload ACIC Result
                        </Button>
                      </>
                    )}
                    <Button icon="download" onClick={download} small>
                      Export
                    </Button>
                  </div>
                </Table.RightFilters>
              </Table.Filters>
              <Table className="u-margin--large">
                <Table.Head>
                  <Table.Row key="header-row">
                    <Table.Header key="id" bulkSelector>
                      {t('common.id')}
                    </Table.Header>
                    <Table.Header key="name" sortKey="candidates.first_name">
                      {t('common.name')}
                    </Table.Header>
                    <Table.Header key="type">Type</Table.Header>
                    {showSubchecks && <Table.Header key="subcheck">Subcheck</Table.Header>}
                    <Table.Header key="status">{t('common.status')}</Table.Header>
                    <Table.Header
                      key="deferred_until"
                      sortKey={
                        showSubchecks
                          ? 'sub_checks.deferred_until'
                          : 'candidate_checks.deferred_until'
                      }>
                      Deferred Until
                    </Table.Header>
                    <Table.Header
                      key="created"
                      sortKey={
                        showSubchecks ? 'sub_checks.created_at' : 'candidate_checks.created_at'
                      }>
                      {t('common.createdAt')}
                    </Table.Header>
                    <Table.Header
                      key="submitted"
                      sortKey={
                        showSubchecks ? 'sub_checks.submitted_at' : 'candidate_checks.submitted_at'
                      }>
                      {t('common.submittedAt')}
                    </Table.Header>
                    <Table.Header
                      key="completed"
                      sortKey={
                        showSubchecks ? 'sub_checks.completed_at' : 'candidate_checks.completed_at'
                      }>
                      {t('common.completedAt')}
                    </Table.Header>
                    <Table.Header key="support_flags">Action</Table.Header>
                    <Table.Header key="assignees">Assignees</Table.Header>
                    <Table.Header key="company">Company Name</Table.Header>
                    <Table.Header
                      key="next_action_at"
                      sortKey={
                        showSubchecks
                          ? 'sub_checks.next_action_at'
                          : 'candidate_checks.next_action_at'
                      }>
                      Next Action
                    </Table.Header>
                    <Table.Header key="internal_history">Notes</Table.Header>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {(candidateChecks || []).map((candidateCheck, index) => {
                    const currentCheckType =
                      checkType ||
                      (checkTypes || []).find(ct => ct.slug === candidateCheck?.check_type);

                    return (
                      <Table.Row
                        key={`check-${candidateCheck.candidate_check_id}-${candidateCheck.sub_check_id ?? 'check'}-${index}`}>
                        <Table.Col
                          bulkSelector
                          selectorId={
                            showSubchecks
                              ? candidateCheck.sub_check_id
                              : candidateCheck.candidate_check_id
                          }>
                          {hasPermission(AdminPermissions.ProcessCandidateChecks) ? (
                            <Link
                              to={`/admin/candidates/${candidateCheck.candidate_id}`}
                              className="u-link dark">
                              {candidateCheck.candidate_check_id ?? candidateCheck.candidate_id}
                            </Link>
                          ) : (
                            <span>
                              {candidateCheck.candidate_check_id ?? candidateCheck.candidate_id}
                            </span>
                          )}
                        </Table.Col>
                        <Table.Col>
                          <div className="d-flex flex-column u-gap-small align-items-start">
                            {`${candidateCheck.first_name} ${candidateCheck.last_name}`}
                            <span className="u-font-size-x-small secondary-text">
                              {candidateCheck.candidate_email}
                            </span>
                          </div>
                        </Table.Col>
                        <Table.Col>
                          <IconComponent
                            size="small"
                            tip={currentCheckType?.title}
                            noBackground
                            type={CC_STATUS_ICON_DISPLAY[candidateCheck.status]?.color}
                            icon={getCheckTypeIcon(currentCheckType?.slug)}
                          />
                        </Table.Col>
                        {showSubchecks && (
                          <Table.Col>{candidateCheck.sub_check_title || '—'}</Table.Col>
                        )}
                        <Table.Col>
                          <div className="d-flex flex-wrap gap-1 align-items-center">
                            <StatusPill
                              text={
                                candidateCheck.status === 'in-progress'
                                  ? candidateCheck.in_progress_sub_status
                                  : candidateCheck.status
                              }
                              status={mapCCStatus(candidateCheck.status)}
                            />

                            {candidateCheck?.bot_status === 'failed' && (
                              <StatusPill text="bot failed" status="error" />
                            )}
                            {candidateCheck?.bot_status === 'submitted' && (
                              <StatusPill text="successfully submitted" status="success" />
                            )}
                            {!!Number(candidateCheck?.overdue_support) && (
                              <StatusPill text={`overdue support`} status={'error'} />
                            )}
                            {!showSubchecks && !!Number(candidateCheck?.overdue_provider) && (
                              <StatusPill text={`overdue provider`} status={'error'} />
                            )}
                            {!!Number(candidateCheck?.has_id_requirements) &&
                              !Number(candidateCheck?.id_requirements_met) && (
                                <StatusPill text="ID required" status="error" />
                              )}
                            <ActivityCountIcons
                              documentCount={candidateCheck?.open_document_request_count}
                              ownedDocumentCount={
                                showSubchecks
                                  ? candidateCheck?.owned_document_request_count
                                  : (Number(candidateCheck?.owned_document_request_count) || 0) +
                                    (Number(candidateCheck?.child_owned_document_request_count) ||
                                      0)
                              }
                              messageCount={candidateCheck?.open_intercom_count}
                            />
                          </div>
                        </Table.Col>
                        <Table.Col>
                          {candidateCheck.deferred_until && (
                            <Moment format="DD/MM/YYYY" parse="YYYY-MM-DD">
                              {candidateCheck.deferred_until}
                            </Moment>
                          )}
                        </Table.Col>
                        <Table.Col>
                          {candidateCheck.created_at && (
                            <Moment format="DD/MM/YYYY">
                              {formatMySqlUtcToLocalDate(candidateCheck?.created_at)}
                            </Moment>
                          )}
                        </Table.Col>
                        <Table.Col>
                          {candidateCheck.submitted_at && (
                            <Moment format="DD/MM/YYYY">
                              {formatMySqlUtcToLocalDate(candidateCheck?.submitted_at)}
                            </Moment>
                          )}
                        </Table.Col>
                        <Table.Col>
                          <div className="d-flex u-gap-small align-items-center">
                            {candidateCheck.completed_at && (
                              <Moment format="DD/MM/YYYY">
                                {formatMySqlUtcToLocalDate(candidateCheck?.completed_at)}
                              </Moment>
                            )}

                            {candidateCheck.upload_id_status === 'no_upload' && (
                              <IconComponent
                                icon={File01Icon}
                                tip="No upload"
                                superfix="×"
                                cornerCount
                                cornerAlert
                                noBackground
                                border={false}
                                type="secondary"
                                size="small"
                                strokeWidth={1.5}
                              />
                            )}
                          </div>
                        </Table.Col>
                        <Table.Col>
                          <ActionRequiredIcons flags={candidateCheck.support_flags} />
                        </Table.Col>
                        <Table.Col>{candidateCheck.support_user || 'Unassigned'}</Table.Col>
                        <Table.Col>
                          <div className="d-flex align-items-center u-gap-small">
                            {candidateCheck.company_name}
                            {NonCustomerAccountTypes.includes(
                              candidateCheck.company_account_type,
                            ) && (
                              <OverlayTrigger
                                overlay={
                                  <Tooltip className="u-capitalize">
                                    {candidateCheck.company_account_type}
                                  </Tooltip>
                                }>
                                <span>
                                  <HugeiconsIcon
                                    icon={BucketIcon}
                                    size="20"
                                    strokeWidth="2"
                                    className="text-warning"
                                  />
                                </span>
                              </OverlayTrigger>
                            )}
                          </div>
                        </Table.Col>
                        <Table.Col>
                          {candidateCheck.next_action_at &&
                            (isDateDue(candidateCheck.next_action_at) ? (
                              <StatusPill
                                text={moment(candidateCheck.next_action_at).format('DD/MM/YYYY')}
                                status="error"
                              />
                            ) : (
                              <Moment format="DD/MM/YYYY" parse="YYYY-MM-DD">
                                {candidateCheck.next_action_at}
                              </Moment>
                            ))}
                        </Table.Col>
                        <Table.Col>
                          <div
                            className="u-link dark"
                            onClick={() => {
                              setSelectedCheckId(candidateCheck?.candidate_check_id);
                              setShowNotes(true);
                            }}>
                            Click for notes
                          </div>
                        </Table.Col>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
                <Table.Pagination {...pagination} />
              </Table>
            </Table.TableContent>
          </Table.Context>
        )}
        {selectedCheckId && (
          <CandidateCheckNotesModal
            checkId={selectedCheckId}
            visible={showNotes}
            onClose={() => setShowNotes(false)}
          />
        )}
        <AnimatedModal
          visible={acicSkippedCount > 0}
          onClose={() => setAcicSkippedCount(null)}
          title="ACIC export: some checks were skipped">
          <p className="mb-0">
            <strong>{acicSkippedCount}</strong>{' '}
            {acicSkippedCount === 1 ? 'check was' : 'checks were'} not included in the XML because
            of missing required data (e.g. missing ID number or agency/state for a licence).{' '}
            {acicSkippedCount === 1 ? 'It remains' : 'They remain'} in Processing and{' '}
            {acicSkippedCount === 1 ? 'was' : 'were'} not set to Waiting on Provider. Resolve the
            issues and export again.
          </p>
        </AnimatedModal>
        <AnimatedModal
          visible={showUploadACICResultModal}
          onClose={() => setShowUploadACICResultModal(false)}
          title="Upload ACIC Result">
          <FilePicker
            createUploadAction={false}
            companyId={user?.company_id}
            signUpload={params => signACICResultUpload(params).unwrap()}
          />
        </AnimatedModal>
      </div>
    </AdminPage>
  );
};

export default AdminCandidateChecks;
